import {
  fetchAllPages,
  signDatasetFiles,
  getDatasetEpisodeFileDownloadUrl,
  getDatasetExtraFileUrl,
  getDatasetMetaFileUrl,
  listDatasetEpisodeFiles,
  listDatasetExtraFiles,
  listDatasetMetaFiles,
  type EpisodeFileResp,
} from "@/api";
import { fetchStorageBody, type DownloadTransport } from "./downloadRunner";

/**
 * meta·extra 목록의 동시 page 요청 수다. 카탈로그 기본값(6)보다 크게 둔다. 동시성을 작게 두면
 * 라운드 수만큼 RTT 가 쌓인다. 사용자가 시작한 bulk 조작이므로 그 순간 화면에는 이 요청뿐이고,
 * 밀려날 다른 요청이 없다. 서명이 없으므로 페이지당 서버 CPU 비용도 없다.
 */
const LIST_PAGE_OPTIONS = { concurrency: 24 } as const;

/**
 * episode 파일 목록의 page 크기다. 서버 상한과 같다.
 *
 * 서버는 이 목록의 page 를 cursor 로 나눈다. 다음 요청에는 앞 응답의 cursor 가 필요하다. 그래서
 * 요청을 차례로 보낸다. page 가 크면 왕복 수가 준다.
 */
const EPISODE_FILE_PAGE_SIZE = 1000;

/**
 * 실제 registry·storage 를 쓰는 transport.
 *
 * 목록은 **서명 없이** 조회한다(BE spec §5). 전체에 인라인 서명해 URL 을 미리 받으면, 파일이
 * 수십만 개일 때 TTL(1시간) 안에 다 받지 못해 큐 뒤쪽 URL 이 모두 만료된다. 그러면 파일마다
 * 단건 재서명이 실행되어, 인라인 서명으로 없애려던 network N+1 이 다시 생긴다. 서명은 쓰기 직전에
 * 창 단위(`signWindow`)로 한다.
 */
export function createDatasetDownloadTransport(datasetUuid: string): DownloadTransport {
  return {
    async collect(signal) {
      const [episodeFiles, metaFiles, extraFiles] = await Promise.all([
        collectEpisodeFiles(datasetUuid, signal),
        fetchAllPages((query) => listDatasetMetaFiles(datasetUuid, query), LIST_PAGE_OPTIONS),
        fetchAllPages((query) => listDatasetExtraFiles(datasetUuid, query), LIST_PAGE_OPTIONS),
      ]);
      signal.throwIfAborted();
      return { episodeFiles, metaFiles, extraFiles };
    },

    async signWindow(entries, signal) {
      const signed = await signDatasetFiles(
        datasetUuid,
        entries.map((entry) => ({ kind: entry.kind, uuid: entry.uuid })),
        signal,
      );
      // 응답은 요청과 같은 길이·순서다(BE 계약). 그래서 인덱스로 맞춘다. 길이가 다르면 계약이
      // 깨진 것이다. 조용히 잘못 짝짓지 않고 그 자리에서 실패시킨다.
      if (signed.length !== entries.length) {
        throw new Error(
          `sign response length ${signed.length} does not match request ${entries.length}`,
        );
      }
      return entries.map((entry, index) => ({
        ...entry,
        url: signed[index].url,
        expiresAt: signed[index].expires_at,
        unavailableReason: signed[index].unavailable_reason,
      }));
    },
    async resign(entry) {
      if (entry.kind === "episode") {
        return (await getDatasetEpisodeFileDownloadUrl(datasetUuid, entry.uuid)).url;
      }
      if (entry.kind === "meta") {
        return (await getDatasetMetaFileUrl(datasetUuid, entry.uuid)).url;
      }
      return (await getDatasetExtraFileUrl(datasetUuid, entry.uuid)).url;
    },
    fetchBody: fetchStorageBody,
  };
}

/**
 * episode 파일 목록을 cursor 로 끝까지 수집한다. cursor 가 null 인 page 가 마지막이다.
 *
 * 요청마다 취소를 먼저 확인한다. 큰 데이터셋은 page 가 수백 개다.
 */
async function collectEpisodeFiles(
  datasetUuid: string,
  signal: AbortSignal,
): Promise<EpisodeFileResp[]> {
  const files: EpisodeFileResp[] = [];
  let after: string | undefined;
  for (;;) {
    signal.throwIfAborted();
    const page = await listDatasetEpisodeFiles(
      datasetUuid,
      { after, size: EPISODE_FILE_PAGE_SIZE },
      { signal },
    );
    files.push(...page.items);
    if (page.cursor === null) return files;
    // 서버는 마지막이 아닌 page 에 size 개를 담는다. 그래서 cursor 가 있는 빈 page 는 계약 위반이다.
    // 다음 요청을 보내지 않고 오류를 던진다.
    if (page.items.length === 0) {
      throw new Error("episode-files cursor did not advance");
    }
    after = page.cursor;
  }
}
